/**
 * Keyboard navigation for the sidebar.
 *
 * ArrowDown/Up: move focus through visible nav items
 * Enter: navigate to focused item
 * Space: toggle pin/unpin
 * ArrowRight: expand module sub-items
 * ArrowLeft: collapse module sub-items
 */

import { useCallback, useState } from "react";

export interface SidebarNavItem {
	path: string;
	label: string;
	canPin?: boolean;
	isModule?: boolean;
	isExpanded?: boolean;
}

interface Options {
	items: SidebarNavItem[];
	onNavigate: (path: string) => void;
	onTogglePin: (path: string) => void;
	onExpandModule: (path: string) => void;
	onCollapseModule: () => void;
}

export function useSidebarKeyboardNav({
	items,
	onNavigate,
	onTogglePin,
	onExpandModule,
	onCollapseModule,
}: Options) {
	const [focusedIndex, setFocusedIndex] = useState(-1);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (items.length === 0) return;

			switch (e.key) {
				case "ArrowDown": {
					e.preventDefault();
					setFocusedIndex((i) => (i + 1) % items.length);
					break;
				}
				case "ArrowUp": {
					e.preventDefault();
					setFocusedIndex((i) => (i - 1 + items.length) % items.length);
					break;
				}
				case "Enter": {
					e.preventDefault();
					const item = items[focusedIndex];
					if (item) onNavigate(item.path);
					break;
				}
				case " ": {
					e.preventDefault();
					const item = items[focusedIndex];
					if (item?.canPin) onTogglePin(item.path);
					break;
				}
				case "ArrowRight": {
					e.preventDefault();
					const item = items[focusedIndex];
					if (item?.isModule && !item.isExpanded) {
						onExpandModule(item.path);
					}
					break;
				}
				case "ArrowLeft": {
					e.preventDefault();
					onCollapseModule();
					break;
				}
				case "Home": {
					e.preventDefault();
					setFocusedIndex(0);
					break;
				}
				case "End": {
					e.preventDefault();
					setFocusedIndex(items.length - 1);
					break;
				}
			}
		},
		[items, focusedIndex, onNavigate, onTogglePin, onExpandModule, onCollapseModule],
	);

	const resetFocus = useCallback(() => setFocusedIndex(-1), []);

	return {
		focusedIndex,
		handleKeyDown,
		resetFocus,
	};
}
